import React, { useEffect, useState } from 'react';
import { arrayOf, shape, string, object, func } from 'prop-types';
import { AspectRatioWrapper, InlineTextButton } from '../../../../components';
import { getCloudinaryBaseUrl } from '../../../../util/cloudinary';
import { isSupabaseAuth } from '../../../../util/supabaseAuth';
import { dataClient } from '../../../../util/supabaseSdk';
import { categoryLevel4KeysWithListings } from '../../../../util/supabaseSdk/listings';
import classNames from 'classnames';

import css from './SubcategoryList.module.css';

const OTHERS_KEY = 'others';

/**
 * Finds the currently selected category from the listingCategories.
 * @param {Array} categories - The full category hierarchy.
 * @param {Object} urlQueryParams - The selected category IDs from the URL.
 * @returns {Object|null} - The selected category object or null if none is selected.
 */
const getSelectedCategory = (categories, urlQueryParams) => {
  let currentCategories = categories;
  let selectedCategory = null;

  for (let i = 1; i <= 3; i++) {
    const categoryKey = `pub_categoryLevel${i}`;
    const selectedCategoryId = urlQueryParams[categoryKey];

    if (!selectedCategoryId) break;

    selectedCategory = currentCategories.find(cat => cat.id === selectedCategoryId);
    if (selectedCategory) {
      selectedCategory.level = i;
      currentCategories = selectedCategory.subcategories || [];
    } else {
      break;
    }
  }

  return selectedCategory;
};

/**
 * Generates search parameters when a subcategory is clicked.
 * - Preserves existing category levels.
 * - Updates the next level with the selected subcategory.
 */
const getUpdatedSearchParams = (urlQueryParams, level, subcategoryId) => {
  if (level < 1 || level > 4) return urlQueryParams;

  const updatedCategories = { ...urlQueryParams };
  updatedCategories[`pub_categoryLevel${level}`] = subcategoryId;

  for (let i = level + 1; i <= 4; i++) {
    updatedCategories[`pub_categoryLevel${i}`] = null;
  }

  return updatedCategories;
};

const SubcategoryItem = props => {
  const { subcategory, selectedCategory, onSubmit, urlQueryParams } = props;
  const isSubcategory = selectedCategory.level > 1;

  const cloudinaryBaseUrl = getCloudinaryBaseUrl({
    variantPrefix: isSubcategory ? 'landscape-crop' : 'landscape-crop2x',
  });
  const imagePath = `${cloudinaryBaseUrl}/categories/categoryLevel${selectedCategory.level + 1}/${
    subcategory.id
  }.jpg`;
  return (
    <InlineTextButton
      key={subcategory.id}
      type="button"
      className={css.subcategoryItem}
      onClick={() =>
        onSubmit(getUpdatedSearchParams(urlQueryParams, selectedCategory.level + 1, subcategory.id))
      }
    >
      <AspectRatioWrapper
        className={css.subcategoryItemImageWrapper}
        aspectBoxClassName={css.subcategoryItemImageWrapperAspectBox}
        width={4}
        height={3}
      >
        {subcategory.id !== OTHERS_KEY ? (
          <img className={css.subcategoryItemImage} src={imagePath} alt={subcategory.name} />
        ) : null}
      </AspectRatioWrapper>
      <span
        className={classNames(css.subcategoryItemName, {
          [css.subcategoryItemNameAbsolute]: isSubcategory,
        })}
      >
        {subcategory.name}
      </span>
    </InlineTextButton>
  );
};

/**
 * On a Category 3 page, which Category 4 tiles lead to listings. Almost no listing
 * has a Category 4 yet (migration 0047 assigns them per Category 3, Rebar first), so
 * showing every tile sent buyers to empty pages, e.g. Rebar > Mild Steel Rebar.
 * { ready: false } while checking (the tiles wait rather than flash and vanish);
 * `listed` null = show every tile (other levels, the Sharetribe SDK, or a failed check).
 */
const useListedLevel4 = (selectedCategory, urlQueryParams) => {
  const subcategories = (selectedCategory && selectedCategory.subcategories) || [];
  const active = isSupabaseAuth() && !!selectedCategory && selectedCategory.level === 3 && subcategories.length > 0;
  const keys = active ? subcategories.map(c => c.id) : [];
  const { pub_categoryLevel1: level1, pub_categoryLevel2: level2, pub_categoryLevel3: level3 } = urlQueryParams;
  const queryKey = active ? [level1, level2, level3, keys.join(',')].join('/') : null;
  const [result, setResult] = useState({ queryKey: null, listed: null });

  useEffect(() => {
    if (!queryKey) return undefined;
    let cancelled = false;
    categoryLevel4KeysWithListings(dataClient, { level1, level2, level3, keys }).then(listed => {
      if (!cancelled) setResult({ queryKey, listed });
    });
    return () => {
      cancelled = true;
    };
    // queryKey covers level1..3 and keys.
  }, [queryKey]);

  if (!queryKey) return { ready: true, listed: null };
  if (result.queryKey !== queryKey) return { ready: false, listed: null };
  return { ready: true, listed: result.listed };
};

const SubcategoryList = props => {
  const { rootClassName, className, listingCategories, urlQueryParams, onSubmit } = props;
  const selectedCategory = getSelectedCategory(listingCategories, urlQueryParams);
  const { ready, listed } = useListedLevel4(selectedCategory, urlQueryParams);

  // Ensure we do not display subcategories for Level 4 selections
  if (
    !selectedCategory ||
    !selectedCategory.subcategories ||
    selectedCategory.subcategories.length === 0 ||
    urlQueryParams.pub_categoryLevel4
  ) {
    return null;
  }

  const classes = classNames(rootClassName || css.root, className);
  const listClasses = classNames(css.subcategoryList, {
    [css.subcategoryListMultipleLevels]: selectedCategory.level > 1,
  });
  const subcategories = !ready
    ? []
    : listed
    ? selectedCategory.subcategories.filter(c => listed.has(c.id))
    : selectedCategory.subcategories;
  return (
    <div className={classes}>
      <h3 className={css.categoryTitle}>{selectedCategory.name}</h3>
      {subcategories.length > 0 ? (
        <div className={listClasses}>
          {subcategories.map(subcategory => (
            <SubcategoryItem
              key={subcategory.id}
              subcategory={subcategory}
              selectedCategory={selectedCategory}
              onSubmit={onSubmit}
              urlQueryParams={urlQueryParams}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
};

SubcategoryList.defaultProps = {
  rootClassName: null,
  className: null,
};

SubcategoryList.propTypes = {
  rootClassName: string,
  className: string,
  listingCategories: arrayOf(
    shape({
      id: string.isRequired,
      name: string.isRequired,
      subcategories: arrayOf(
        shape({
          id: string.isRequired,
          name: string.isRequired,
        })
      ),
    })
  ).isRequired,
  urlQueryParams: object.isRequired,
  onSubmit: func.isRequired,
};

export default SubcategoryList;
